import React from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Star, Award, Package, Heart, BarChart, LayoutGrid, Plus } from 'lucide-react';
import StatCard from './StatCard';
import { SystemStats } from '../../types';
import { useTheme } from '../../contexts/ThemeContext';

interface GamesContentProps {
  loading: boolean;
  statsLoading: boolean;
  statistics: any | null;
  handleUpdateAverageRatings: () => void;
}

const GamesContent: React.FC<GamesContentProps> = ({ 
  loading, 
  statsLoading, 
  statistics, 
  handleUpdateAverageRatings 
}) => {
  const { theme } = useTheme();

  return (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    {/* Gerenciamento de Jogos */}
    <div className={`rounded-lg shadow-md p-6 ${theme === 'dark' ? 'bg-gray-800 border border-gray-700 text-gray-300' : 'bg-white text-gray-900'}`}>
      <h2 className={`text-xl font-semibold mb-4 flex items-center ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
        <LayoutGrid className={`h-5 w-5 mr-2 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-500'}`} /> Gerenciar Jogos
      </h2>
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
          <Link 
            to="/admin/games/new" 
            className={`inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white 
                        ${theme === 'dark' ? 'bg-indigo-500 hover:bg-indigo-600' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
            <Plus className="h-4 w-4 mr-1" /> Adicionar Novo Jogo
          </Link>
          <Link 
            to="/admin/games" 
            className={`inline-flex items-center justify-center px-4 py-2 border text-sm font-medium rounded-md 
                        ${theme === 'dark' ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`}>
            <LayoutGrid className="h-4 w-4 mr-1" /> Listar Todos os Jogos
          </Link>
        </div>
        <div className="mt-4">
          <button 
            onClick={handleUpdateAverageRatings} 
            disabled={loading} 
            className={`inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white 
                        ${theme === 'dark' ? 'bg-green-500 hover:bg-green-600 disabled:bg-green-700' : 'bg-green-600 hover:bg-green-700 disabled:bg-green-300'}`}>
            {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Star className="h-4 w-4 mr-1" />}
            Atualizar Médias de Avaliações
          </button>
        </div>
      </div>
    </div>
    
    {/* Estatísticas de Jogos */}
    <div className={`rounded-lg shadow-md p-6 ${theme === 'dark' ? 'bg-gray-800 border border-gray-700 text-gray-300' : 'bg-white text-gray-900'}`}>
      <h2 className={`text-xl font-semibold mb-4 flex items-center ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
        <BarChart className={`h-5 w-5 mr-2 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-500'}`} /> Estatísticas de Jogos
      </h2>
      {statsLoading ? (
        <div className="flex justify-center items-center h-40"><Loader2 className={`w-8 h-8 animate-spin ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-500'}`} /></div>
      ) : statistics ? (
        <div className="space-y-6">
          <div>
            <h3 className={`text-lg font-medium mb-3 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>Avaliações</h3>
            <div className="grid grid-cols-2 gap-4">
              <StatCard icon={Star} value={statistics.total_ratings} label="Total de Avaliações" color="border-amber-500 dark:border-amber-400" size="sm" />
              <StatCard icon={Award} value={statistics.avg_rating ? statistics.avg_rating.toFixed(1) : 'N/A'} label="Média Geral" color="border-yellow-500 dark:border-yellow-400" size="sm" />
            </div>
          </div>
          <div className={`${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} border-t pt-4`}>
            <h3 className={`text-lg font-medium mb-3 ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>Coleções de Jogos</h3>
            <div className="grid grid-cols-2 gap-4">
              <StatCard icon={Package} value={statistics.total_collections} label="Em Coleções" color="border-green-500 dark:border-green-400" size="sm" />
              <StatCard icon={Heart} value={statistics.total_wishlists} label="Em Wishlists" color="border-purple-500 dark:border-purple-400" size="sm" />
            </div>
            <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-700'} mt-4`}>
              <span className="font-semibold">Média:</span> {statistics.avg_collection_size ? Number(statistics.avg_collection_size).toFixed(1) : 'N/A'} jogos por usuário
            </p>
          </div>
        </div>
      ) : (
        <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Falha ao carregar estatísticas.</p>
      )}
    </div>
  </div>
)};

export default GamesContent; 